import http from 'hub-http/clients/apiClient';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import formatName from 'I18n/utils/formatName';
import { registerMutation, registerQuery, useMutation, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import { updateAchievementVisibilityRpc } from '../../../__generated__/chirp/com/hubspot/credentials/rpc/AchievementRpc';
import { useCreateAchievementMutation } from '../../../__generated__/chirp/com/hubspot/credentials/rpc/AchievementRpc.hooks';
import { rpcClient } from '../../client';
import { updateCache } from '../../utils/DataFetchingClientCache.new';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import UsageTracker from '../../utils/UsageTracker';
import { slugify, usernameFromEmail } from '../../utils/utils';
import { useUserInfo } from '../accounts';
import { useAllAchievements } from './achievementQueries';
import { GET_ALL_ACHIEVEMENTS_FOR_USER_QUERY } from './achievementQueries';
import { GET_PUBLIC_ACHIEVEMENT_V2_QUERY } from '../../../__generated__/chirp/com/hubspot/credentials/rpc/AchievementPublicRpc.dfc.new';
export const GET_CREDENTIAL_DEFINITION = registerQuery({
  fieldName: 'credentialDefinition',
  args: ['credentialDefinitionId'],
  fetcher({
    credentialDefinitionId
  }) {
    return http.get(`/credentials/consumers/v2/credential-definitions/${credentialDefinitionId}`);
  }
});
export const useCredentialDefinition = credentialDefinitionId => {
  const {
    data
  } = useQuery(GET_CREDENTIAL_DEFINITION, {
    variables: {
      credentialDefinitionId: credentialDefinitionId
    },
    skip: !credentialDefinitionId
  });
  const name = useMemo(() => {
    if (!data) return undefined;
    const metadata = data.metadata;
    if (Array.isArray(metadata) && metadata.length > 0) {
      var _metadata$;
      const enMeta = metadata.find(m => m.language === 'en' || m.language === 'EN');
      return (enMeta === null || enMeta === void 0 ? void 0 : enMeta.title) || ((_metadata$ = metadata[0]) === null || _metadata$ === void 0 ? void 0 : _metadata$.title);
    }
    return undefined;
  }, [data]);
  return {
    icon: data === null || data === void 0 ? void 0 : data.icon,
    name
  };
};
export const UPDATE_ACHIEVEMENT_VISIBILITY_CREDENTIALS_SERVICE = registerMutation({
  fieldName: 'updateAchievementVisibilityForShare',
  args: ['achievementPublicId', 'visibility'],
  fetcher({
    achievementPublicId,
    visibility
  }) {
    return rpcClient.call(updateAchievementVisibilityRpc, {
      request: {
        achievementPublicId,
        visibility: visibility
      }
    });
  }
});
export const useAchievementVisibilityToggle = achievement => {
  var _achievement$publicId;
  const isPrivate = !!achievement && achievement.visibility === 'PRIVATE';
  const targetVisibility = isPrivate ? 'PUBLIC' : 'PRIVATE';
  const [mutate, {
    loading: updateLoading
  }] = useMutation(UPDATE_ACHIEVEMENT_VISIBILITY_CREDENTIALS_SERVICE, {
    variables: {
      achievementPublicId: (_achievement$publicId = achievement === null || achievement === void 0 ? void 0 : achievement.publicId) !== null && _achievement$publicId !== void 0 ? _achievement$publicId : '',
      visibility: targetVisibility
    },
    update() {
      if (achievement) {
        updateCache(GET_ALL_ACHIEVEMENTS_FOR_USER_QUERY, draft => {
          var _draft$achievements;
          const match = (_draft$achievements = draft.achievements) === null || _draft$achievements === void 0 ? void 0 : _draft$achievements.find(a => a.publicId === achievement.publicId);
          if (match) {
            match.visibility = targetVisibility;
          }
        }, {});
      }
    }
  });
  const toggleVisibility = useCallback(() => {
    if (!achievement) return;
    void mutate({
      refetchQueries: targetVisibility === 'PUBLIC' && achievement.publicId ? [{
        query: GET_PUBLIC_ACHIEVEMENT_V2_QUERY,
        variables: {
          publicId: achievement.publicId
        }
      }] : []
    });
  }, [achievement, mutate, targetVisibility]);
  const isDisabled = !achievement || updateLoading || isPrivate;
  return {
    isPrivate,
    isDisabled,
    toggleVisibility,
    updateLoading
  };
};
const useAchievementByGrantedCredentialId = grantedCredentialId => {
  const {
    allAchievements,
    allAchievementsLoading,
    allAchievementsError
  } = useAllAchievements();
  const foundAchievement = useMemo(() => {
    return (allAchievements || []).find(achievement => achievement.grantedCredentialId === grantedCredentialId);
  }, [allAchievements, grantedCredentialId]);
  return {
    achievement: foundAchievement,
    achievementsLoading: allAchievementsLoading,
    achievementsError: allAchievementsError
  };
};
export const useMaybeCreateAchievementByCredential = (grantedCredentialId, grantedCredentialCode) => {
  const {
    achievement,
    achievementsLoading,
    achievementsError
  } = useAchievementByGrantedCredentialId(grantedCredentialId);
  const {
    userInfo
  } = useUserInfo();
  const slug = useMemo(() => {
    var _userInfo$user$emails;
    if (!grantedCredentialCode || !userInfo) return undefined;
    const {
      firstName,
      lastName
    } = userInfo.user;
    const email = (_userInfo$user$emails = userInfo.user.emails[0]) !== null && _userInfo$user$emails !== void 0 ? _userInfo$user$emails : '';
    const userName = firstName || lastName ? formatName({
      firstName,
      lastName
    }) : usernameFromEmail(email);
    return `${slugify(userName)}/${slugify(grantedCredentialCode)}`;
  }, [grantedCredentialCode, userInfo]);
  const [createAchievement, {
    loading: createLoading
  }] = useCreateAchievementMutation({
    variables: grantedCredentialCode && slug ? {
      grantedCredentialCode,
      slug
    } : undefined,
    onCompleted: ({
      result
    }) => {
      UsageTracker.trackStandalone('Learning Center Interaction', {
        action: 'create-achievement',
        publicAchievementId: result.achievement.publicId,
        privacy: readUnhandledEnumCase(result.achievement.visibility)
      });
    },
    refetchQueries: [{
      query: GET_ALL_ACHIEVEMENTS_FOR_USER_QUERY
    }]
  });
  const hasAttemptedCreate = useRef(false);
  const isServerError = achievementsError && achievementsError.status !== 404;
  useEffect(() => {
    if (grantedCredentialCode && slug && !achievementsLoading && !isServerError && !createLoading) {
      if (!achievement && !hasAttemptedCreate.current) {
        hasAttemptedCreate.current = true;
        void createAchievement();
      }
    }
  }, [achievement, achievementsLoading, createAchievement, createLoading, grantedCredentialCode, slug, isServerError]);
  return {
    achievement,
    achievementsLoading,
    achievementsError
  };
};